import { useMemo, useState, useDeferredValue } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Eye, CalendarPlus, Plus } from 'lucide-react';
import type { PaginationState, SortingState, ColumnDef } from '@tanstack/react-table';
import type { TaskWithRelations } from '@vagusx/clients';
import { TablePage, SortableHeader } from '@/components/table-page';
import { Button } from '@/components/ui/button';
import {
  useReferralTasks,
  formatPatientType,
  canResolveReferralDocument,
} from '@/hooks/use-referrals';
import { ScheduleAppointmentDrawer } from '@/pages/tasks/overview/action-drawers/ScheduleAppointmentDrawer';
import { ExportAppointmentsButton } from './ExportAppointmentsButton';
import { NewReferralDialog } from '@/pages/documents/NewReferralDialog';
import {
  ReferredByCell,
  DateReferredCell,
  UrgencyCell,
  StatusCell,
  AppointmentTypeCell,
  ScheduledTimeCell,
  ProvidersCell,
} from './referral-cells';
import { useReferralProviderRanking } from '@/hooks/use-appointment-type-matching';
import { ReferralSheet } from './ReferralSheet';

/**
 * Referrals quick-view — a worklist of open referrals (referral-labeled tasks)
 * with the at-a-glance fields a scheduler needs, plus two actions: view the
 * full Task Overview, or open the same Schedule Appointment panel used there.
 *
 * Reuses the task inbox search endpoint (filtered to referral labels + open
 * statuses); the document-derived, appointment-type, and first-available
 * columns are fetched lazily per visible row (see referral-cells.tsx).
 */
export default function ReferralsPage(): React.ReactElement {
  // 25 to match the task inbox and bound the per-row lazy fan-out (each visible
  // row issues ~3 requests for its document / match / availability columns).
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });
  // Only `createdAt` is server-sortable here — the other columns are derived
  // per-row and can't be ordered by the list query.
  const [sorting, setSorting] = useState<SortingState>([{ id: 'dateReferred', desc: true }]);
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearch = useDeferredValue(searchTerm);
  const [showNewReferral, setShowNewReferral] = useState(false);

  // Task whose Schedule Appointment panel is open. The drawer is rendered once
  // at page level in controlled mode and driven by this state.
  const [scheduleTask, setScheduleTask] = useState<TaskWithRelations | null>(null);

  // WHICH REFERRAL IS OPEN LIVES IN THE URL, not in component state. The sheet
  // was previously driven by a `TaskWithRelations` held in `useState`, which
  // meant the address bar said `/referrals` no matter what was on screen —
  // nothing could be linked to, reloaded, or handed to someone else. `?referral=<id>`
  // makes the URL describe the view.
  const navigate = useNavigate();
  const { referral: openReferralId } = useSearch({ from: '/_authenticated/referrals/' });

  const openReferral = (task: TaskWithRelations): void => {
    // A push, not a replace, so Back closes the sheet instead of leaving the
    // page — which is what a reader who opened one row expects.
    void navigate({ to: '/referrals', search: (prev) => ({ ...prev, referral: task.id }) });
  };
  const closeReferral = (): void => {
    // `call` goes with it. It identifies an interaction on THIS referral, so
    // leaving it set means the next referral opened would immediately surface
    // the previous one's call detail — a different patient's call, on top of
    // the wrong panel.
    void navigate({
      to: '/referrals',
      search: (prev) => ({ ...prev, referral: undefined, call: undefined }),
    });
  };

  const { data, isLoading, error } = useReferralTasks({
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    searchTerm: deferredSearch || undefined,
    sortBy: 'createdAt',
    sortOrder: sorting[0]?.desc === false ? 'asc' : 'desc',
  });

  // Ranked provider pool for every row on THIS page, in one request. Rows are
  // referrals awaiting scheduling, so for most of them the pool is the only
  // provider answer that exists yet.
  const pageTaskIds = useMemo(() => (data?.items ?? []).map((t) => t.id), [data?.items]);
  const providerRanking = useReferralProviderRanking(pageTaskIds);

  // The row already on screen, so the sheet need not fetch it; it fetches by
  // id for a cold deep link (see `ReferralSheet`).
  const loadedReferral = useMemo(
    () => (data?.items ?? []).find((t) => t.id === openReferralId) ?? null,
    [data?.items, openReferralId]
  );

  const handleSearchChange = (value: string): void => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const columns = useMemo<ColumnDef<TaskWithRelations>[]>(
    () => [
      {
        accessorKey: 'patientName',
        header: 'Patient',
        meta: { headerClassName: 'w-[13%]', cellClassName: 'truncate' },
        cell: ({ row }) => (
          <span className="font-medium" title={row.original.patientName ?? undefined}>
            {row.original.patientName ?? 'Unknown patient'}
          </span>
        ),
      },
      {
        id: 'dateReferred',
        header: ({ column }) => <SortableHeader column={column}>Date referred</SortableHeader>,
        meta: { headerClassName: 'w-[9%]', cellClassName: 'truncate' },
        cell: ({ row }) => <DateReferredCell task={row.original} />,
      },
      {
        id: 'referredBy',
        header: 'Referred by',
        meta: { headerClassName: 'w-[13%]', cellClassName: 'overflow-hidden' },
        cell: ({ row }) => <ReferredByCell task={row.original} />,
      },
      {
        id: 'patientType',
        header: 'New / Existing',
        meta: { headerClassName: 'w-[6%]', cellClassName: 'truncate' },
        cell: ({ row }) => {
          const type = formatPatientType(row.original.labels);
          return type ? <span>{type}</span> : <span className="text-muted-foreground">—</span>;
        },
      },
      {
        id: 'urgency',
        header: 'Urgency',
        meta: { headerClassName: 'w-[6%]' },
        cell: ({ row }) => <UrgencyCell task={row.original} />,
      },
      {
        id: 'status',
        header: 'Status',
        meta: { headerClassName: 'w-[8%]', cellClassName: 'overflow-hidden' },
        cell: ({ row }) => <StatusCell task={row.original} />,
      },
      {
        id: 'appointmentType',
        header: 'Appointment type',
        meta: { headerClassName: 'w-[12%]', cellClassName: 'overflow-hidden' },
        cell: ({ row }) => (
          <AppointmentTypeCell
            task={row.original}
            ranking={providerRanking.byTaskId.get(row.original.id)}
            isLoading={providerRanking.isLoading}
          />
        ),
      },
      {
        id: 'scheduledTime',
        header: 'Scheduled time',
        // 12%: `table-fixed` makes these hard widths, and a full
        // `MMM d, yyyy h:mm a` stamp was being clipped mid-time at 9%.
        meta: { headerClassName: 'w-[12%]', cellClassName: 'truncate' },
        cell: ({ row }) => <ScheduledTimeCell task={row.original} />,
      },
      {
        id: 'providers',
        header: 'Provider(s)',
        // Wraps rather than truncates: a pool is up to `max_pool_size` names, and
        // a clipped list silently hides the very providers this column exists to
        // show. `whitespace-normal` overrides TableCell's `whitespace-nowrap` via
        // tailwind-merge — no `components/ui/` change.
        meta: {
          headerClassName: 'w-[15%]',
          cellClassName: 'whitespace-normal break-words',
        },
        cell: ({ row }) => (
          <ProvidersCell
            task={row.original}
            ranking={providerRanking.byTaskId.get(row.original.id)}
            isLoading={providerRanking.isLoading}
          />
        ),
      },
      {
        id: 'actions',
        header: '',
        meta: { headerClassName: 'w-[6%]' },
        cell: ({ row }) => {
          // Scheduling needs a resolvable referral document; without one the
          // call would fail server-side, so disable the action up front (mirrors
          // the gating on the Task Overview button).
          const canSchedule = canResolveReferralDocument(row.original);
          return (
            <div
              className="flex justify-end gap-1"
              // Stop these from also triggering the row's view click.
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                title="View details"
                onClick={() => openReferral(row.original)}
              >
                <Eye className="h-4 w-4" />
                <span className="sr-only">View details</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={!canSchedule}
                title={
                  canSchedule ? 'Schedule appointment' : 'No referral document to schedule from'
                }
                onClick={() => setScheduleTask(row.original)}
              >
                <CalendarPlus className="h-4 w-4" />
                <span className="sr-only">Schedule appointment</span>
              </Button>
            </div>
          );
        },
      },
    ],
    // The hook's return object is a new literal every render; depend on the
    // stable values inside it or this memo never hits.
    [providerRanking.byTaskId, providerRanking.isLoading]
  );

  return (
    <TablePage
      title="Referrals"
      description="Open referrals awaiting scheduling"
      headerAction={
        <div className="flex items-center gap-2">
          <Button size="sm" className="gap-1.5" onClick={() => setShowNewReferral(true)}>
            <Plus className="h-4 w-4" />
            New referral
          </Button>
          <ExportAppointmentsButton />
        </div>
      }
      columns={columns}
      data={data?.items ?? []}
      total={data?.total}
      isLoading={isLoading}
      error={error}
      searchPlaceholder="Search referrals by patient, title, or label..."
      onRowClick={openReferral}
      emptyMessage="No open referrals."
      errorMessage="Failed to load referrals. Please try again."
      pagination={pagination}
      onPaginationChange={setPagination}
      onSearchChange={handleSearchChange}
      sorting={sorting}
      onSortingChange={setSorting}
      tableClassName="table-fixed"
    >
      {scheduleTask && (
        // key on the task id so switching referrals remounts the drawer with a
        // fresh call session (idempotency key, phase) rather than reusing the
        // previous task's state.
        <ScheduleAppointmentDrawer
          key={scheduleTask.id}
          task={scheduleTask}
          open
          onOpenChange={(next) => {
            if (!next) setScheduleTask(null);
          }}
        />
      )}

      {/* "View details" → the same task work panel as the inbox, but on its own
          in a slide-over (no inbox list). */}
      <ReferralSheet taskId={openReferralId} loaded={loadedReferral} onClose={closeReferral} />

      <NewReferralDialog open={showNewReferral} onOpenChange={setShowNewReferral} />
    </TablePage>
  );
}
